import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Plus, Users, UserCheck, UserX, TrendingUp } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Badge,
} from "@b2b/ui-components";

import { SubAccountTable } from "./components/subAccountTable";
import { SubAccountForm } from "./components/SubAccountForm";
import { SubAccount, SubAccountFormData } from "./types/subAccount";
import { sampleSubAccounts } from "./mockSubAccount";

export const SubAccountManagement = () => {
  const [subAccounts, setSubAccounts] = useState<SubAccount[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<SubAccount | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // const { toast } = useToast();

  useEffect(() => {
    // Load initial mock data
    setSubAccounts(sampleSubAccounts);
  }, []);

  const stats = {
    total: subAccounts.length,
    active: subAccounts.filter((acc) => acc.status === "active").length,
    suspended: subAccounts.filter((acc) => acc.status === "suspended").length,
    pending: subAccounts.filter((acc) => acc.status === "pending").length,
  };

  const handleAddAccount = () => {
    setEditingAccount(null);
    setIsFormOpen(true);
  };

  const handleEditAccount = (account: SubAccount) => {
    setEditingAccount(account);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (formData: SubAccountFormData) => {
    setIsLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));

    try {
      if (editingAccount) {
        setSubAccounts((prev) =>
          prev.map((acc) =>
            acc.id === editingAccount.id
              ? {
                  ...acc,
                  ...formData,
                }
              : acc
          )
        );
        // toast({
        //   title: "Account Updated",
        //   description: `${formData.username} has been successfully updated.`,
        // });
      } else {
        const newAccount: SubAccount = {
          id: (subAccounts.length + 1 + Math.random()).toString(),
          ...formData,
          status: "pending",
          lastLogin: "Never",
          createdAt: new Date().toISOString(),
          createdBy: "Current User",
        };
        setSubAccounts((prev) => [newAccount, ...prev]);
        // toast({
        //   title: "Account Created",
        //   description: `${formData.username} has been successfully created.`,
        // });
      }
      setIsFormOpen(false);
      setEditingAccount(null);
    } catch {
      // toast({
      //   title: "Error",
      //   description: "Something went wrong. Please try again.",
      //   variant: "destructive",
      // });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuspendAccount = async (id: string) => {
    const account = subAccounts.find((acc) => acc.id === id);
    if (!account) return;

    setSubAccounts((prev) =>
      prev.map((acc) =>
        acc.id === id ? { ...acc, status: "suspended" as const } : acc
      )
    );
    // toast({
    //   title: "Account Suspended",
    //   description: `${account.username} has been suspended.`,
    //   variant: "destructive",
    // });
  };

  const handleActivateAccount = async (id: string) => {
    const account = subAccounts.find((acc) => acc.id === id);
    if (!account) return;

    setSubAccounts((prev) =>
      prev.map((acc) =>
        acc.id === id ? { ...acc, status: "active" as const } : acc
      )
    );
    // toast({
    //   title: "Account Activated",
    //   description: `${account.username} has been activated.`,
    // });
  };

  const handleDeleteAccount = async (id: string) => {
    const account = subAccounts.find((acc) => acc.id === id);
    if (!account) return;

    if (
      window.confirm(
        `Are you sure you want to delete ${account.username}? This action cannot be undone.`
      )
    ) {
      setSubAccounts((prev) => prev.filter((acc) => acc.id !== id));
      // toast({
      //   title: "Account Deleted",
      //   description: `${account.username} has been permanently deleted.`,
      //   variant: "destructive",
      // });
    }
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingAccount(null);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 sm:p-6 rounded-lg shadow-md gap-4"
      >
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">
            Sub-Account Management
          </h1>
          <p className="text-gray-500 text-sm sm:text-base">
            Manage your team members and their permissions
          </p>
        </div>
        <Button
          onClick={handleAddAccount}
          className="gap-2 bg-orange-500 hover:bg-orange-600 text-white w-full sm:w-auto"
        >
          <Plus className="h-4 w-4" /> Add Sub-Account
        </Button>
      </motion.div>

      {/* Stats Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
      >
        <Card className="border-l-4 border-l-orange-500 shadow-md hover:shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Accounts
            </CardTitle>
            <Users className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500 shadow-md hover:shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
            <UserCheck className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">
              {stats.active}
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-400 shadow-md hover:shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Suspended</CardTitle>
            <UserX className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">
              {stats.suspended}
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-yellow-400 shadow-md hover:shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <TrendingUp className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-500">
              {stats.pending}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Team Members Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="shadow-md border rounded-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-semibold">
              <Users className="h-5 w-5" />
              Team Members
              <Badge
                variant="secondary"
                className="ml-auto bg-gray-100 border-none"
              >
                {stats.total} total
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {/* Make table horizontally scrollable on mobile */}
            <SubAccountTable
              subAccounts={subAccounts}
              onEdit={handleEditAccount}
              onSuspend={handleSuspendAccount}
              onActivate={handleActivateAccount}
              onDelete={handleDeleteAccount}
            />
          </CardContent>
        </Card>
      </motion.div>

      {/* Add/Edit Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent
          className="w-full max-w-md sm:max-w-4xl max-h-[90vh] overflow-y-auto p-0 rounded-lg shadow-lg bg-white"
          showCloseButton={false}
        >
          <DialogHeader className="p-6 pb-0">
            <DialogTitle className="text-lg font-semibold">
              {editingAccount ? "Edit Sub-Account" : "Add New Sub-Account"}
            </DialogTitle>
          </DialogHeader>
          <div className="p-6 max-h-[calc(90vh-100px)] overflow-y-auto">
            <SubAccountForm
              initialData={editingAccount}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              isLoading={isLoading}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
